import { Dayjs } from 'dayjs'
import { ID } from '@/types/common'
import {
  BillingMode,
  ClassRecordRules,
  ClassMonthlySummary,
  ClassRecordKind,
  ClassRecordStatus,
  ClassType,
  GenerateClassRecordTemplate,
  ManualCreateFromCurrentItem,
} from './types'

export type GenerateTemplateItem = Omit<GenerateClassRecordTemplate, 'teacherId'> & {
  localId: string
  teacherId?: ID
  coursePrice?: number
}

export type QuickCreateItem = Omit<ManualCreateFromCurrentItem, 'teacherId'> & {
  localId: string
  teacherId?: ID
  coursePrice?: number
  durationMinutes: number
  note: string
}

export type BatchEditAttendanceMode = 'unchanged' | ClassRecordStatus
export type BatchEditTeacherValue = ID | 'unchanged'

export const emptySummary = (
  studentId: ID | undefined,
  month: string,
): ClassMonthlySummary => ({
  studentId: studentId ?? 0,
  month,
  plannedTrainingCount: 0,
  plannedGroupCount: 0,
  plannedAttendedTrainingCount: 0,
  plannedAttendedGroupCount: 0,
  manualAttendedTrainingCount: 0,
  manualAttendedGroupCount: 0,
  leaveMakeupTrainingCount: 0,
  leaveMakeupGroupCount: 0,
  leaveConvertedToMoneyTrainingCount: 0,
  leaveConvertedToMoneyGroupCount: 0,
  convertedToMoneyTrainingCount: 0,
  convertedToMoneyGroupCount: 0,
  makeupGeneratedMinutes: 0,
  makeupUsedMinutes: 0,
  makeupUsedGroupLessons: 0,
  convertedToMoneyAmount: 0,
  currentMakeupBalanceMinutes: 0,
  currentMakeupBalanceGroupLessons: 0,
  makeupBalanceLastRecalculatedAt: null,
})

export const defaultClassRecordRules = (): ClassRecordRules => ({
  id: null,
  makeupRetentionMonths: 2,
  defaultCoursePrice: 200,
  defaultClassDurationMinutes: 40,
  note: null,
})

export const createTemplateRow = (
  defaultCoursePrice = 200,
): GenerateTemplateItem => ({
  localId: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
  classType: ClassType.PERSONAL,
  billingMode: BillingMode.PER_CLASS,
  coursePrice: defaultCoursePrice,
})

export const createQuickCreateRow = (
  defaultCoursePrice = 200,
  defaultDurationMinutes = 40,
  recordKind = ClassRecordKind.MANUAL,
): QuickCreateItem => ({
  localId: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
  classType: ClassType.PERSONAL,
  billingMode: BillingMode.PER_CLASS,
  recordKind,
  classRecordStatus: ClassRecordStatus.ATTENDED,
  durationMinutes: defaultDurationMinutes,
  coursePrice: defaultCoursePrice,
  note: '',
})

export const getMonthRange = (month: Dayjs): [Dayjs, Dayjs] => [
  month.startOf('month'),
  month.endOf('month'),
]

export const toDraftTemplate = (
  template: GenerateClassRecordTemplate,
  defaultCoursePrice = 200,
  isAllDayPricing = false,
): GenerateTemplateItem => ({
  localId: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
  classType: template.classType,
  billingMode: template.billingMode,
  teacherId: template.teacherId,
  coursePrice:
    isAllDayPricing || template.billingMode === BillingMode.ALL_DAY || template.billingMode === BillingMode.GIFT
      ? undefined
      : template.coursePrice ?? defaultCoursePrice,
})

export const toQuickCreateItem = (
  template: GenerateClassRecordTemplate,
  defaultCoursePrice = 200,
  defaultDurationMinutes = 40,
  recordKind = ClassRecordKind.MANUAL,
): QuickCreateItem => ({
  localId: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
  classType: template.classType,
  billingMode: BillingMode.PER_CLASS,
  recordKind,
  classRecordStatus: ClassRecordStatus.ATTENDED,
  durationMinutes: defaultDurationMinutes,
  teacherId: template.teacherId,
  teacherName: template.teacherName ?? null,
  coursePrice: template.coursePrice ?? defaultCoursePrice,
  note: '',
})
